/**
 * PURPOSE: Vista secundaria "Designaciones" dentro de Programación (FTR-0001).
 * RESPONSIBILITIES: Mostrar los partidos de una fase/fecha (jornada) real de Programación —
 *   mismos idtypephase/nro_fecha, nunca una fecha de calendario independiente — con sus
 *   roles/oficiales asignados y estado DRAFT/OFFICIAL en una tabla compacta; incluir la descarga PDF.
 * COLLABORATORS: matchOfficialsService.js, ProgramacionArea.jsx (dueño de la config de fase/fecha,
 *   ya cargada vía POST /config_category — reutilizada aquí por props, no reconsultada).
 * ROUTE: winscore/src/area/ProgramacionArea/DesignacionesTab.jsx
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FaFilePdf } from "react-icons/fa";

import {
  downloadDesignationsPdf,
  getApiErrorMessage,
  listDesignationsByDate,
} from "../../services/matchOfficialsService.js";

const formatShortDate = (isoDate) => {
  if (!isoDate) return "—";
  const [year, month, day] = String(isoDate).split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}`;
};

const DesignacionesTab = ({ idevent, idsport, nroFechasGrupo, nroEtapasFinal, fechas, etapas, isHaveRepechaje }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState("1");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [payload, setPayload] = useState({ roles: [], matches: [] });

  const hasGroupPhase = Number(nroFechasGrupo) > 0;
  const hasFinalPhase = Number(nroEtapasFinal) > 0;

  // Same idtypephase resolution ProgramacionArea.jsx's own getCurrentIdTypePhase() uses — real
  // scheduling identifiers, not a second/invented definition of phase.
  const getIdTypePhase = (index) => {
    if (hasFinalPhase && !hasGroupPhase) return 2;
    return index === 0 ? 1 : 2;
  };

  // Reset selection whenever the event/sport (and therefore its phase config) changes, defaulting
  // to whichever phase actually exists.
  useEffect(() => {
    setSelectedIndex(hasFinalPhase && !hasGroupPhase ? 1 : 0);
    setSelectedDate("1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idevent, idsport, nroFechasGrupo, nroEtapasFinal]);

  const dateOptions =
    selectedIndex === 0
      ? (fechas || []).map((f) => ({ value: f, label: `Fecha ${f}` }))
      : (etapas || []).map((et, i) => ({
          value: String(i + 1),
          label: isHaveRepechaje && i === 0 ? "Repechaje" : et,
        }));

  const phaseName = selectedIndex === 0 ? "Fase de Grupos" : "Fase de Eliminación";
  const dateLabel = dateOptions.find((opt) => opt.value === selectedDate)?.label || `Fecha ${selectedDate}`;
  const phaseLabel = `${phaseName} · ${dateLabel}`;

  const load = async () => {
    if (!idevent || !idsport || !selectedDate) return;
    setLoading(true);
    try {
      const data = await listDesignationsByDate({
        idevent,
        idsport,
        idtypephase: getIdTypePhase(selectedIndex),
        nroFecha: selectedDate,
      });
      setPayload({ roles: data.roles || [], matches: data.matches || [] });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudieron cargar las designaciones."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idevent, idsport, selectedIndex, selectedDate]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDesignationsPdf({
        idevent,
        idsport,
        idtypephase: getIdTypePhase(selectedIndex),
        nroFecha: selectedDate,
        phaseLabel,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo generar el PDF de designaciones."));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Compact phase/jornada selector — real Programación identifiers, single row, no large
          vertically-stacked headers. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        {hasGroupPhase && hasFinalPhase ? (
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 bg-white text-xs font-semibold">
            <button
              type="button"
              onClick={() => {
                setSelectedIndex(0);
                setSelectedDate("1");
              }}
              className={`px-2.5 py-1 transition-colors ${
                selectedIndex === 0 ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              Fase de Grupos
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedIndex(1);
                setSelectedDate("1");
              }}
              className={`px-2.5 py-1 transition-colors ${
                selectedIndex === 1 ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              Fase de Eliminación
            </button>
          </div>
        ) : (
          <span className="whitespace-nowrap text-xs font-bold uppercase tracking-wide text-slate-500">
            {phaseName}
          </span>
        )}

        {dateOptions.length > 0 && <span className="text-slate-300">·</span>}

        <div className="flex flex-wrap gap-1">
          {dateOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSelectedDate(opt.value)}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                selectedDate === opt.value
                  ? "bg-indigo-600 text-white"
                  : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
          {dateOptions.length === 0 && (
            <span className="text-xs text-slate-400">Sin fechas configuradas para esta fase.</span>
          )}
        </div>

        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || payload.matches.length === 0}
          className="ml-auto inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60"
        >
          <FaFilePdf className="h-3.5 w-3.5" />
          {downloading ? "Generando..." : "Descargar PDF"}
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500">Cargando...</div>
      ) : payload.matches.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
          No hay partidos programados para {phaseLabel.toLowerCase()}.
        </div>
      ) : (
        // Scrolls within this container (both axes) rather than the whole page — a sticky header
        // keeps columns aligned while scrolling, and a bounded max-height keeps a long match list
        // from making the overall Designaciones section unnecessarily tall.
        <div
          className="w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm"
          style={{ maxHeight: "68vh" }}
        >
          <table className="w-full min-w-full border-separate border-spacing-0 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr>
                <th className="w-12 whitespace-nowrap border-b border-slate-200 px-2 py-2.5 text-center text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  N°
                </th>
                <th className="w-16 whitespace-nowrap border-b border-slate-200 px-2 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Fecha
                </th>
                <th className="w-14 whitespace-nowrap border-b border-slate-200 px-2 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Hora
                </th>
                <th className="w-40 whitespace-nowrap border-b border-slate-200 px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Sede
                </th>
                <th className="min-w-[180px] whitespace-nowrap border-b border-slate-200 px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Partido
                </th>
                {payload.roles.map((role) => (
                  <th
                    key={role.idrole}
                    className="min-w-[130px] whitespace-nowrap border-b border-slate-200 px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500"
                    title={role.name}
                  >
                    {role.name}
                  </th>
                ))}
                <th className="w-24 whitespace-nowrap border-b border-slate-200 px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody>
              {payload.matches.map((match, index) => (
                <tr
                  key={match.idmatch}
                  className={`transition-colors hover:bg-indigo-50/40 ${index % 2 === 1 ? "bg-slate-50/60" : "bg-white"}`}
                >
                  <td className="whitespace-nowrap border-b border-slate-100 px-2 py-1.5 text-center text-slate-500">
                    {match.nroMatch ?? "—"}
                  </td>
                  <td className="whitespace-nowrap border-b border-slate-100 px-2 py-1.5 text-slate-700">
                    {formatShortDate(match.date)}
                  </td>
                  <td className="whitespace-nowrap border-b border-slate-100 px-2 py-1.5 text-slate-700">
                    {match.time || "—"}
                  </td>
                  <td
                    className="max-w-[160px] truncate border-b border-slate-100 px-3 py-1.5 text-slate-700"
                    title={match.venue?.name || undefined}
                  >
                    {match.venue?.name || "—"}
                  </td>
                  <td className="whitespace-nowrap border-b border-slate-100 px-3 py-1.5 font-semibold text-slate-800">
                    {/* Always a single line — width pressure is absorbed by the table's own
                        horizontal scroll container, never by wrapping/stacking this cell. */}
                    {match.home?.abrev || match.home?.name || "Pendiente"}
                    <span className="mx-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">vs</span>
                    {match.away?.abrev || match.away?.name || "Pendiente"}
                  </td>
                  {match.assignments.map((assignment) => (
                    <td
                      key={assignment.idrole}
                      className="whitespace-nowrap border-b border-slate-100 px-3 py-1.5 text-slate-700"
                      title={assignment.official?.fullName || undefined}
                    >
                      {assignment.official?.fullName || "—"}
                    </td>
                  ))}
                  <td className="whitespace-nowrap border-b border-slate-100 px-3 py-1.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${match.status === "OFFICIAL"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                        }`}
                    >
                      {match.status === "OFFICIAL" ? "Oficial" : "Borrador"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DesignacionesTab;
